import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { processCoreModelResponse } from "@/store/-utils";
import CollectionsApi from "@/store/collections/-api";

const emptyFeedFactory: () => CreatorCollectionFeed = () => ({ ids: [], meta: null });

@Module({
  name: "collections",
  stateFactory: true,
  namespaced: true,
})
export default class CollectionsStore extends VuexModule {
  collections: CreatorCollectionMap = {};
  feeds: CreatorCollectionFeeds = {};

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.creatorCollectionFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  get hasFeedBeenFetched() {
    return (feedName: string) => {
      const feed = this.creatorCollectionFeed(feedName);
      return feed && feed.meta !== null && feed.ids.length > 0;
    };
  }

  get hasCollectionInfoBeenFetched() {
    return (collectionId: string) => {
      return collectionId in this.collections;
    };
  }

  get creatorCollectionFeed() {
    return (feedName: string) => this.feeds[feedName] ?? emptyFeedFactory();
  }

  @Action({ rawError: true })
  async fetchCollectionInfo({ id }: { id: string }) {
    const api = new CollectionsApi(this.store.$axios);
    const result = await api.getCollectionInfo({ id });
    const collectionInfo = result.data.collection;

    if (collectionInfo !== undefined) {
      this.setCollectionInfo(collectionInfo);
    }
  }

  @Action({ rawError: true })
  async fetchCollectionItems({ id, overwrite = false }: { id: string; overwrite?: boolean }) {
    if (!this.getFeedHasMorePages(id) && overwrite === false) {
      return;
    }

    const api = new CollectionsApi(this.store.$axios);
    const data: Parameters<(typeof api)["getCollectionItems"]>[0] = { id };
    const meta = this.feeds[id]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
      data.limit = meta.limit;
    }

    const result = await api.getCollectionItems(data);

    const { items, postsV2 } = result.data;
    const { ltks, profiles, products, mediaObjects } = postsV2 || {};

    await processCoreModelResponse(this.store, { ltks, profiles, products, mediaObjects });

    if (items.length > 0) {
      this.setCollectionItems({
        collectionId: id,
        collectionItems: items,
        meta: result.data.meta || null,
        overwrite,
      });

      const ltkIds = items
        .map((item) => item.externalId)
        // temporary fix: remove duplicate items
        .filter((value, index, self) => self.indexOf(value) === index);
      this.setCreatorCollectionItemFeed({ feedName: id, ids: ltkIds, meta: result.data.meta || null, overwrite });
    }
  }

  @Action({ rawError: true })
  async fetchCreatorCollectionFeed({
    id,
    displayName,
    overwrite,
  }: {
    id: string;
    displayName: string;
    overwrite?: boolean;
  }) {
    const api = new CollectionsApi(this.store.$axios);
    const data: Parameters<(typeof api)["getCreatorCollections"]>[0] = { id };
    const meta = this.feeds[displayName]?.meta || null;
    if (!this.getFeedHasMorePages(displayName) && overwrite === false) {
      return;
    }
    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
      data.limit = meta.limit;
    }
    const result = await api.getCreatorCollections(data);

    // Set core models before feed lists
    await processCoreModelResponse(this.store, result.data);
    const ids = result.data.collections.map((collection) => collection.collectionId);
    this.setCreatorCollectionFeed({ feedName: displayName, ids, meta: result.data.meta, overwrite });
  }

  @Mutation
  setCollectionInfo(collectionInfo: CollectionInfo) {
    Vue.set(this, "collections", Object.assign(this.collections, { [collectionInfo.collectionId]: collectionInfo }));
  }

  @Mutation
  setManyCollectionInfo({ collections }: { collections: CollectionsMap }) {
    Vue.set(this, "collections", Object.assign(this.collections, collections));
  }

  @Mutation
  setCollectionItems({
    collectionId,
    collectionItems,
    meta,
    overwrite = false,
  }: {
    collectionId: string;
    collectionItems: CollectionItem[];
    meta: CollectionItemMeta | null;
    overwrite?: boolean;
  }) {
    const collection = this.collections[collectionId];
    if (collection !== undefined) {
      if (collection.items === undefined) {
        Vue.set(this.collections, collectionId, Object.assign(collection, { items: collectionItems, meta }));
      } else {
        const start = overwrite ? 0 : collection.items.length;
        const length = overwrite ? collection.items.length : 0;
        this.collections[collectionId]?.items.splice(start, length, ...collectionItems);

        Vue.set(this.collections, collectionId, Object.assign(collection, { meta: meta ?? null }));
      }
    }
  }

  @Mutation
  setCreatorCollectionItemFeed({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: CollectionItemMeta | null;
    overwrite?: boolean;
  }) {
    // Create feed if it doesn't exist
    if (this.feeds[feedName] === undefined) {
      Vue.set(this.feeds, feedName, { ids: [], meta: null });
    }

    // Add items to feed
    const start = overwrite ? 0 : this.feeds[feedName].ids.length;
    const length = overwrite ? this.feeds[feedName].ids.length : 0;
    this.feeds[feedName].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feedName], "meta", meta ?? null);
  }

  @Mutation
  setCreatorCollectionFeed({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: CollectionItemMeta | null;
    overwrite?: boolean;
  }) {
    // Create feed if it doesn't exist
    if (this.feeds[feedName] === undefined) {
      Vue.set(this.feeds, feedName, { ids: [], meta: null });
    }

    // Add items to feed
    const start = overwrite ? 0 : this.feeds[feedName].ids.length;
    const length = overwrite ? this.feeds[feedName].ids.length : 0;
    this.feeds[feedName].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feedName], "meta", meta ?? null);
  }
}

export const getCollectionsStore = (store: Store<unknown>) => {
  return getModule(CollectionsStore, store);
};
